import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { SiteHeaderEn } from "@/components/SiteHeaderEn";
import { SiteFooterEn } from "@/components/SiteFooterEn";
import { LeadSection } from "@/components/LeadSection";
import { cases } from "@/lib/cases-data";
import { enCases } from "@/lib/cases-en";

const SITE = "https://shopify-freelancer.net";
const TITLE = "Shopify Cases: Stores I Have Worked On";
const DESCRIPTION =
  "Selected Shopify projects across D2C, B2B and configurator stores, starting point, levers implemented and the metrics the effect is measured on.";

export const Route = createFileRoute("/en/cases")({
  component: CasesEn,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}/en/cases` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/en/cases` },
      { rel: "alternate", hrefLang: "de", href: "https://shopify-freelancer.net/cases" },
      { rel: "alternate", hrefLang: "en", href: "https://shopify-freelancer.net/en/cases" },
      { rel: "alternate", hrefLang: "x-default", href: "https://shopify-freelancer.net/cases" },
      { rel: "alternate", hrefLang: "de", href: `${SITE}/cases` },
      { rel: "alternate", hrefLang: "en", href: `${SITE}/en/cases` },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: TITLE,
          itemListElement: cases.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: `${c.shop}, ${enCases[c.slug]?.industry ?? c.industry}`,
            url: `${SITE}/en/cases`,
          })),
        }),
      },
    ],
  }),
});

const monthsEn: Record<string, string> = { Januar: "January", März: "March", April: "April", Juli: "July" };
const enPeriod = (v?: string) =>
  (v ?? "").replace(/Januar|März|April|Juli/, (m) => monthsEn[m] ?? m);

function CasesEn() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeaderEn />
      <main>
        <section className="veil border-b border-border/60 py-16 md:py-20">
          <div className="mx-auto max-w-6xl px-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.34em] text-primary">Cases</p>
            <h1 className="mt-5 max-w-3xl text-4xl font-bold leading-[1.08] md:text-5xl">
              Stores where I worked on the{" "}
              <span className="text-signal">conversion rate</span>
            </h1>
            <p className="mt-5 max-w-xl text-muted-foreground">
              D2C, B2B and configurator stores. Per project: the starting point, the levers
              implemented and the metrics the effect shows up in.
            </p>
          </div>
        </section>

        <section className="py-14">
          <div className="mx-auto grid max-w-6xl gap-10 px-5">
            {cases.map((c) => {
              const en = enCases[c.slug];
              if (!en) return null;
              return (
                <article
                  key={c.slug}
                  className="overflow-hidden rounded-lg border border-border bg-card md:grid md:grid-cols-2"
                >
                  <div className="flex items-center justify-center border-b border-border bg-muted/20 p-5 md:border-b-0 md:border-r md:p-7">
                    <img
                      src={c.image}
                      alt={en.imageAlt}
                      width={c.width}
                      height={c.height}
                      loading="lazy"
                      decoding="async"
                      className="max-h-[320px] w-full rounded-md border border-border object-contain object-top"
                    />
                  </div>
                  <div className="p-6 md:p-8">
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                      {en.industry}
                    </p>
                    <h2 className="mt-2 text-xl font-semibold md:text-2xl">{c.shop}</h2>
                    <a
                      href={c.url}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-primary"
                    >
                      {c.domain} <ArrowUpRight className="h-4 w-4" />
                    </a>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{en.claim}</p>

                    {c.metrics.length > 0 && (
                      <div className="mt-5 grid grid-cols-2 gap-3">
                        {c.metrics.map((m) => (
                          <div key={m.label} className="contents">
                            <div className="rounded-md border border-border bg-background px-4 py-3">
                              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                                Before{m.beforeLabel ? ` · ${enPeriod(m.beforeLabel)}` : ""}
                              </p>
                              <p className="mt-1 text-2xl font-bold tabular-nums">{m.before}</p>
                              <p className="text-xs text-muted-foreground">Conversion rate</p>
                            </div>
                            <div className="rounded-md border border-signal/40 bg-signal/10 px-4 py-3">
                              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                                After{m.afterLabel ? ` · ${enPeriod(m.afterLabel)}` : ""}
                              </p>
                              <p className="text-signal mt-1 text-2xl font-bold tabular-nums">{m.after}</p>
                              <p className="text-xs text-muted-foreground">Conversion rate</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <h3 className="mt-5 text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                      Starting point
                    </h3>
                    {en.situation.map((s) => (
                      <p key={s} className="mt-2 text-sm leading-relaxed text-muted-foreground">
                        {s}
                      </p>
                    ))}

                    <h3 className="mt-5 text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                      Levers implemented
                    </h3>
                    <ul className="mt-2 grid list-disc gap-1.5 pl-4 text-sm text-muted-foreground">
                      {en.work.map((w) => (
                        <li key={w}>{w}</li>
                      ))}
                    </ul>

                  </div>
                </article>
              );
            })}
          </div>

          <div className="mx-auto mt-10 max-w-6xl px-5">
            <Link to="/cases" hrefLang="de" className="text-sm font-semibold text-primary">
              Diese Seite auf Deutsch →
            </Link>
          </div>
        </section>

        <LeadSection source="en-cases" lang="en" />
      </main>
      <SiteFooterEn />
    </div>
  );
}
